import { useEffect, useRef } from "react";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { useAccessPurchase } from "./accessPurchase";
import { VendorTicketsSheet } from "./VendorTicketsSheet";

/** How long a press opening something ignores the next one. */
const PRESS_COOLDOWN_MS = 1000;

/**
 * What a 1-tap access's button opens: the purchase sheet
 * (VendorTicketsSheet) over the Details, for an event's tickets and a
 * merch product's sizes alike, drawn from the access's purchase
 * (`useAccessPurchase`: `undefined` while the access is read, `null` for
 * none; `failed` and `retry`), returned as it is.
 *
 * `open(fallback)` opens one sheet for a double tap, and runs `fallback`
 * (an event's external link) where the access offers no purchase; while
 * the access is still read, or its read failed, it opens nothing. An old
 * press for an access no longer shown opens nothing.
 */
export function useVendorSheet(accessId: string) {
  const bottomSheetManager = useBottomSheetManager();
  const offered = useAccessPurchase(accessId);
  const { purchase } = offered;
  // One mounted occurrence of the access: returning to the same access
  // must not adopt an old press.
  const shown = useRef<{ accessId: string } | null>(null);
  useEffect(() => {
    shown.current = { accessId };
    return () => {
      shown.current = null;
    };
  }, [accessId]);
  const pressing = useRef<{ scope: { accessId: string } } | null>(null);

  const open = async (fallback?: () => Promise<void>) => {
    // A double tap opens one sheet (or one link).
    const scope = shown.current;
    if (!scope || scope.accessId !== accessId || pressing.current?.scope === scope) return;
    if (purchase === undefined) return;
    const press = { scope };
    pressing.current = press;
    try {
      if (purchase) {
        bottomSheetManager.open(
          <VendorTicketsSheet accessId={accessId} purchase={purchase} onPurchase={offered.update} />,
          { mode: "push" },
        );
        return;
      }
      await fallback?.();
    } finally {
      setTimeout(() => {
        if (pressing.current === press) pressing.current = null;
      }, PRESS_COOLDOWN_MS);
    }
  };

  return { ...offered, open };
}
